import { Box, Typography } from "@mui/material";
import { SubscriptionState } from "../../proto/api_pb";

export interface SubscriptionStatusBannerProps {
  subscriptionState: number | undefined;
}

// Copy for each non-healthy subscription state. SUBSCRIBED / ON_TRIAL /
// UNSPECIFIED render nothing — this banner only ever adds context for a
// problem state, it never replaces the existing Trial Mode badge or the
// storage-usage UI it sits above.
const STATE_COPY: Partial<Record<number, string>> = {
  [SubscriptionState.SUBSCRIPTION_STATE_PAST_DUE]: "Payment past due",
  [SubscriptionState.SUBSCRIPTION_STATE_UNPAID]:
    "Payment failed — update your card",
  [SubscriptionState.SUBSCRIPTION_STATE_CANCELED]: "Subscription canceled",
  [SubscriptionState.SUBSCRIPTION_STATE_INCOMPLETE_EXPIRED]:
    "Subscription setup incomplete",
};

// PAST_DUE / UNPAID / INCOMPLETE_EXPIRED use --danger, CANCELED uses --warn.
const DANGER_STATES = new Set<number>([
  SubscriptionState.SUBSCRIPTION_STATE_PAST_DUE,
  SubscriptionState.SUBSCRIPTION_STATE_UNPAID,
  SubscriptionState.SUBSCRIPTION_STATE_INCOMPLETE_EXPIRED,
]);

/**
 * Shared, additive banner for non-healthy `subscription_state` values.
 * Renders nothing for SUBSCRIBED/ON_TRIAL/UNSPECIFIED. Color treatment
 * mirrors the claudesign StatusPill color-mix formula (border 35%, bg 14%
 * over --surface, text 72%-to-white) using only existing --danger/--warn
 * tokens from theme/tokens.css — no new hex values introduced.
 */
export const SubscriptionStatusBanner: React.FC<
  SubscriptionStatusBannerProps
> = ({ subscriptionState }) => {
  if (subscriptionState === undefined || subscriptionState === null) {
    return null;
  }

  const copy = STATE_COPY[subscriptionState];
  if (!copy) {
    return null;
  }

  const c = DANGER_STATES.has(subscriptionState) ? "var(--danger)" : "var(--warn)";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        fontSize: "12.5px",
        fontWeight: 600,
        padding: "5px 11px 5px 9px",
        borderRadius: "20px",
        border: `1px solid color-mix(in oklab, ${c} 35%, transparent)`,
        background: `color-mix(in oklab, ${c} 14%, var(--surface))`,
        marginBottom: "10px",
        width: "fit-content",
      }}
    >
      <Box
        sx={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          background: c,
          flex: "0 0 7px",
        }}
      />
      <Typography
        sx={{
          fontSize: "12.5px",
          fontWeight: 600,
          color: `color-mix(in oklab, ${c} 72%, white)`,
        }}
      >
        {copy}
      </Typography>
    </Box>
  );
};

export default SubscriptionStatusBanner;
